import { ThemeV2 } from "../util/types";

const transitionMessages = [
  "Saving your response...",
  "Checking your response...",
  "Processing your answers...",
  "Selecting the next question...",
  "Loading the next question...",
];

export const defaultThemeV2: ThemeV2 = {
  theme_version: "v2",
  show_progress: false,
  show_grid_lines: false,
  transition_messages: transitionMessages,
  tokens: {
    color: {
      action: { primary: "#49AE8A", primaryText: "#FFFFFF" },
      text: {
        primary: "#111111",
        secondary: "#808080",
        inverse: "#FFFFFF",
        accent: "#49AE8A",
      },
      surface: {
        canvas: "#F7F8FA",
        card: "#FFFFFF",
        overlay: "rgba(0,0,0,0.5)",
      },
      border: { default: "#D9DCE3", strong: "#AAB1BF" },
      feedback: { error: "#F19C99" },
    },
    typography: {
      fontFamily: "Roboto, sans-serif",
      size: { xs: 14, sm: 14, md: 16, lg: 18, xl: 20 },
      weight: { regular: 400, medium: 500, bold: 700 },
      lineHeight: { tight: 1.2, normal: 1.4, relaxed: 1.6 },
    },
    shape: { radius: { none: 0, xs: 2, sm: 2, md: 2, lg: 2, pill: 999 } },
    spacing: { "1": 4, "2": 8, "3": 12, "4": 16, "5": 20, "6": 24 },
    elevation: {
      "1": "0 1px 2px rgba(0,0,0,0.12)",
      "2": "0 2px 6px rgba(0,0,0,0.18)",
      "3": "0 4px 12px rgba(0,0,0,0.22)",
      "message-shadow": "4px 4px 1px rgba(0, 0, 0, 0.3)",
    },
  },
  copy: {
    header: { line1: "Talking with", line2: "Shanna Researcher" },
    redirect: "We'll send you back in {seconds} seconds...",
    close: "You can close this window now.",
    disqualification:
      "We're sorry but you don't qualify for this survey today.",
    hints: {
      waiting: "Shanna Researcher is Typing...",
      multichoice: "Select your option...",
      multiselect: "Select your options...",
      others: "Enter your message...",
      multitext: "Enter your response...",
      customAnswer: "Please enter",
    },
    actions: {
      send: "Send",
      confirm: "Ok",
      close: "Close",
      remove: "Remove",
      continue: "Continue",
      choose: "Choose",
      editResponse: "Edit {label}",
      playVideo: "Play video",
      pauseVideo: "Pause video",
      acknowledge: "OK",
      next: "Next",
      cancel: "Cancel",
      moveUp: "Move {option} up",
      moveDown: "Move {option} down",
      submit: "Submit",
      undo: "Undo",
      openInBrowser: "Open in browser",
      dontKnow: "I don't know",
      retryVideo: "Retry video",
      auto: "Auto",
      autoAnswer: "Submit default/test answer",
      comment: "Comment",
      show: "Show",
      hide: "Hide",
      deleteStoredValue: "Delete {question}",
    },
    labels: {
      option: "Option {index}",
      chooseOption: "Choose option {index}",
      number: "number",
      rowOption: "Row {row}: {option}",
      rowRange: "Row {row}: {left} to {right}",
      rowRating: "{row}: rating",
      rowRatingScale: "{row}: rating scale",
      rating: "Rating",
      ratingScale: "Rating scale",
      surveyImage: "Survey image",
      questionImage: "Question Image",
      landscapeImage: "Landscape Image",
      avatar: "message.username",
      hlsVideo: "HLS Video Section",
      cipherPlayer: "VdoCipher Player",
      yourAnswer: "Your answer",
      surveyTitle: "Take a survey",
      response: "Response {label}",
      total: "Total",
      or: "Or",
      conjointOptions: "Conjoint options",
      videoDial: "Video dial",
      videoPlayback: "Video playback",
      textToHighlight: "Text to highlight",
      phoneNumber: "Phone number",
      surveyProgress: "Survey progress",
      clientLogo: "Client logo",
      image: "image",
      imagePreview: "Image preview",
      previewImage: "Preview image",
      preview: "Preview {image}",
      closeImagePreview: "Close image preview",
      embeddedContent: "Embedded content",
      storedValues: "Stored values",
      star: "{value} Star",
      stars: "{value} Stars",
      emptyRating: "Empty",
    },
    status: {
      loading: "Loading",
      transitions: transitionMessages,
      secondsRemaining: "{seconds} seconds remaining",
      countdown: "{seconds}s",
      highlighted: "highlighted",
      notHighlighted: "not highlighted",
      highlightWord: "{word}, {index} of {total}, {state}",
      highlighterHelp:
        "Use arrow keys to choose a word, then Space to highlight or remove it.",
      groupProgressVisible: "{completed}/{total} completed · {pending} pending",
      groupProgress:
        "{completed} of {total} questions completed, {pending} pending",
      answered: "Answered",
      readyToSubmit: "Ready to submit",
      needsCorrection: "Needs correction",
      pending: "Pending",
      removed: "Removed {target}",
      dontKnow: "Don't know",
      defaultResponse: "Default response",
      imageViewed: "Image viewed",
      videoViewed: "Video viewed",
    },
    dropdown: {
      label: "Search or select an option",
      placeholder: "Type to search",
      noOptions: "No matching options",
      clear: "Clear",
      close: "Close",
      open: "Open",
      loading: "Loading…",
    },
    phone: {
      label: "Phone",
      placeholder: "1 (702) 123-4567",
      search: "search",
      noResults: "No entries to show",
      countryNames: {},
    },
    validation: {
      response: "Enter a response.",
      number: "Enter a number.",
      phone: "Enter a phone number.",
      selectAvailable: "Select an available option.",
      selectCustomAnswer: "Enter a custom answer.",
      selectAtLeastOne: "Select at least one option.",
      tooManyOptions: "Too many options selected.",
      invalidOption: "Invalid option selected.",
      exclusiveOptions: "Exclusive options must stand alone.",
      multiSelectCustomAnswer: "Enter the custom answer.",
      rankRange: "Rank {min} to {max} options.",
      highlight: "Highlight at least one part of the text.",
      gridSelect: "Select one available option for each row.",
      gridMultiSelect: "Select at least one option for each row.",
      invalidRowSelection: "Invalid row selection.",
      rating: "Select a rating.",
      rateEachRow: "Rate each row.",
      wholeNumbers: "Enter a whole number in every cell.",
      chooseEachRow: "Choose one option for each row.",
      chooseTwo: "Choose two different items.",
      chooseOne: "Choose one option.",
      minResponses: "Enter at least {min} responses.",
      videoSaving: "Saving video response. Please wait.",
      videoWatch: "Watch the video before continuing.",
    },
    errors: {
      generic: "Something went wrong. Please try again later.",
      dialUnavailable: "Dial sample transport is unavailable.",
      sessionClosed: "Survey session is closed.",
      commentUnavailable: "Comment transport is unavailable.",
      unsupportedQuestion: "Unsupported survey question type: {type}",
      requestTimeout: "Survey request timed out",
      requestCancelled: "Survey request cancelled",
      requestDeadline: "Survey request deadline reached",
      httpStatus: "HTTP error! Status: {status}",
    },
  },
  assets: {
    header: { icon: "themes/shanna/shanna.jpeg" },
    chat: { background: "themes/shanna/background.jpeg" },
  },
  layout: { orientation: "landscape" },
  custom_css: "",
};
